import { Link } from "@fluentui/react-components";
import { LegalMeta } from "apps/diagnostics/common/constants";
import { Constants } from "common/constants";
import { ExternalLayout } from "layouts/externalLayout/externalLayout";
import { useStyles } from "./termsOfUsePage.styles";

export const TermsOfUsePage = (): JSX.Element => {
  const styles = useStyles();

  return (
    <ExternalLayout>
      <div className={styles.page}>
        <a href="#tos-start" className={styles.skipLink}>
          Skip to terms content
        </a>

        <header aria-label="Page header" className={styles.header}>
          <h1 className={styles.title}>
            Terms of Use and Data Usage Statement
          </h1>
          <span className={styles.updated} aria-live="polite">
            Last updated:{" "}
            <time dateTime={LegalMeta.termsLastUpdated}>
              {LegalMeta.termsLastUpdated}
            </time>
          </span>
        </header>

        <nav aria-label="On this page" className={styles.toc}>
          <p className={styles.tocHeading}>On this page</p>
          <ul className={styles.tocList}>
            <li>
              <Link className={styles.tocLink} href="#introduction">
                Introduction
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#ai">
                Artificial Intelligence
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#collection">
                Data Collection
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#use">
                How We Use Your Data
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#storage">
                Data Storage and Retention
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#restrictions">
                Important Restrictions
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#ip">
                Intellectual Property
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#liability">
                Limitation of Liability
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#rights">
                Your Rights
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#termination">
                Termination
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#modifications">
                Modifications
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#contact">
                Contact Information
              </Link>
            </li>
          </ul>
        </nav>

        <div id="tos-start" />

        <section id="introduction" className={styles.section}>
          <h2 className={styles.sectionTitle}>Introduction</h2>
          <p className={styles.paragraph}>
            <br />
            Welcome to the North Highland NH Diagnostic IQ System. By accessing
            and using this survey, you agree to the following terms and
            conditions, which outline how North Highland collects, processes,
            stores, and protects the information you provide.
          </p>
        </section>

        <section id="ai" className={styles.section}>
          <h2 className={styles.sectionTitle}>Artificial Intelligence</h2>
          <p className={styles.paragraph}>
            <br />
            You acknowledge and accept that this survey system utilizes
            artificial intelligence (AI) technology to conduct interviews,
            generate responses, and create follow-up questions.
          </p>
        </section>

        <section id="collection" className={styles.section}>
          <h2 className={styles.sectionTitle}>Data Collection</h2>
          <p className={styles.paragraph}>
            <br />
            <strong>Survey Responses:</strong> We collect your text and/or audio
            responses to survey questions.
            <br />
            <strong>Access Method:</strong> You access this survey through a
            unique identifier (GUID) provided specifically to you.
            <br />
            <strong>Audio Recordings:</strong> If you choose to use the audio
            response feature, your voice will be temporarily recorded.
            <br />
            <strong>Transcriptions:</strong> Audio responses are automatically
            transcribed to text using Microsoft Azure Foundry speech services.
          </p>
        </section>

        <section id="use" className={styles.section}>
          <h2 className={styles.sectionTitle}>How We Use Your Data</h2>
          <p className={styles.paragraph}>
            <br />
            Your responses will be used to generate AI-powered follow-up
            questions during the survey. The information collected will be
            analyzed to gather qualitative insights for the specific business
            purpose outlined in your survey invitation. Survey data may be
            aggregated with other responses to identify patterns and trends. We
            do not use your responses to make automated decisions about you as
            an individual.
          </p>
        </section>

        <section id="storage" className={styles.section}>
          <h2 className={styles.sectionTitle}>Data Storage and Retention</h2>
          <p className={styles.paragraph}>
            <br />
            Text responses are stored in our secure database. Any audio
            recordings are temporarily stored only for transcription and deleted
            promptly after processing. Transcribed data is exported for
            analysis. We maintain appropriate technical and organizational
            measures to protect your information.
          </p>
        </section>

        <section id="restrictions" className={styles.section}>
          <h2 className={styles.sectionTitle}>Important Restrictions</h2>
          <p className={styles.paragraph}>
            <br />
            Do not provide any Personal Health Information (PHI) or details
            about medical conditions. Limit sharing of Personally Identifiable
            Information (PII) to what is strictly necessary for the survey
            purpose. This survey is neither designed to nor intended to collect
            PII, PHI, or sensitive personal data.
          </p>
        </section>

        <section id="ip" className={styles.section}>
          <h2 className={styles.sectionTitle}>Intellectual Property</h2>
          <p className={styles.paragraph}>
            <br />
            All intellectual property rights related to the North Highland
            AI-Powered Survey System, including AI prompts, algorithms, and any
            generated content, belong to North Highland. You may not reproduce,
            modify, distribute, or create derivative works based on the system
            without our prior written consent.
          </p>
        </section>

        <section id="liability" className={styles.section}>
          <h2 className={styles.sectionTitle}>Limitation of Liability</h2>
          <p className={styles.paragraph}>
            <br />
            The North Highland NH Diagnostic IQ System is provided on an “as-is”
            basis. We do not guarantee the accuracy, completeness, or
            reliability of the AI's responses. Under no circumstances shall
            North Highland be liable for any direct, indirect, incidental,
            consequential, or punitive damages arising out of your use of this
            system.
          </p>
        </section>

        <section id="rights" className={styles.section}>
          <h2 className={styles.sectionTitle}>Your Rights</h2>
          <p className={styles.paragraph}>
            <br />
            You may decline to participate in this survey without consequence.
            You have the right to request information about what data we hold
            about you. You may request deletion of your data, subject to our
            legitimate business needs and legal requirements.
          </p>
        </section>

        <section id="termination" className={styles.section}>
          <h2 className={styles.sectionTitle}>Termination</h2>
          <p className={styles.paragraph}>
            <br />
            We reserve the right to terminate your access to the North Highland
            NH Diagnostic IQ System at any time and for any reason without prior
            notice.
          </p>
        </section>

        <section id="modifications" className={styles.section}>
          <h2 className={styles.sectionTitle}>Modifications</h2>
          <p className={styles.paragraph}>
            <br />
            We may update these Terms of Use and Data Usage Statement from time
            to time. Any changes will be effective immediately upon posting the
            revised terms on this system. Your continued use of the system after
            the changes constitutes your acceptance of the new terms.
          </p>
        </section>

        <section id="contact" className={styles.section}>
          <h2 className={styles.sectionTitle}>Contact Information</h2>
          <p className={styles.paragraph}>
            <br />
            For questions about these Terms of Use and Data Usage Statement, or
            to exercise your rights regarding your data, please contact your
            North Highland engagement team or{" "}
            <Link href={Constants.ISSUE_URL_MAILTO}>
              nhhelpu@northhighland.com
            </Link>
            .
          </p>
        </section>
      </div>
    </ExternalLayout>
  );
};
